import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Briefcase } from "lucide-react";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

const team = [
  { name: "Cyril Dumont", role: "Président & Ingénieur froid et climatisation", experience: "20 ans d'expérience" },
  { name: "Cyril Pires",  role: "Chargé d'affaire",                              experience: "20 ans d'expérience" },
  { name: "Karim Dupont", role: "Chargé d'affaire & Technicien",                 experience: "5 ans d'expérience" }
];

const stats = [
  { value: "20",    label: "Années d'expérience" },
  { value: "12",    label: "Techniciens" },
  { value: "150k€", label: "Budget moyen projet" }
];

const badges = ["Techniciens certifiés", "Garantie décennale", "SAV 7j/7", "Devis gratuit"];

export default function EquipePage() {
  return (
    <>
      <Helmet>
        <title>Notre équipe | FLOW - Climatisation & Ventilation</title>
        <meta name="description" content="L'équipe FLOW : Cyril Dumont (Président & Ingénieur froid et climatisation), Cyril Pires (Chargé d'affaire), Karim Dupont (Chargé d'affaire & Technicien)." />
        <link rel="canonical" href="https://flow-air.com/equipe" />
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#F5F7FA] to-white py-16 md:py-24" data-testid="equipe-hero">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto">
            <span className="caption mb-4 block">Notre équipe</span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0E1A2B] mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              L'équipe <span className="text-[#00AFFF]">FLOW</span>
            </h1>
            <p className="text-lg text-[#475569] leading-relaxed">
              FLOW est composée de 12 techniciens et d'une direction technique
              dirigée par des fondateurs cumulant 20 années d'expérience en
              installation de climatisation et ventilation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container-flow">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                data-testid={`equipe-stat-${i}`}
              >
                <div className="text-3xl md:text-4xl font-bold text-[#00AFFF] mb-2" style={{ fontFamily: 'Sora, sans-serif' }}>
                  {stat.value}
                </div>
                <div className="text-sm text-[#475569]">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Members */}
      <section className="py-16 md:py-24 bg-[#F5F7FA]" data-testid="equipe-members">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center mb-12 max-w-2xl mx-auto">
            <span className="caption mb-4 block">Profils</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0E1A2B]" style={{ fontFamily: 'Sora, sans-serif' }}>
              Direction &amp; chargés d'affaire
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {team.map((member, index) => (
              <motion.article
                key={member.name}
                className="bg-white rounded-2xl p-8 shadow-sm border border-[#E2E8F0] text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`equipe-member-${index}`}
              >
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#00AFFF] to-[#0099DD] flex items-center justify-center mb-6 mx-auto">
                  <span className="text-white font-bold text-2xl" style={{ fontFamily: 'Sora, sans-serif' }}>
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-[#0E1A2B] mb-1">{member.name}</h3>
                <p className="text-[#00AFFF] text-sm font-semibold mb-3">{member.role}</p>
                <div className="flex items-center justify-center gap-2 text-[#475569] text-xs">
                  <Briefcase size={12} />
                  <span>{member.experience}</span>
                </div>
              </motion.article>
            ))}
          </div>

          <p className="text-center text-sm italic text-[#94A3B8] mt-6" data-testid="equipe-photos-note">
            Photos de l'équipe à venir
          </p>

          {/* 4 badges globaux */}
          <motion.div {...fadeInUp} className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto mt-12">
            {badges.map((item, i) => (
              <div key={i} className="flex items-center justify-center gap-2 py-3 px-4 bg-white rounded-lg shadow-sm">
                <div className="w-6 h-6 rounded-md bg-gradient-to-br from-[#00AFFF]/15 to-[#00AFFF]/5 flex items-center justify-center">
                  <Check size={12} className="text-[#00AFFF]" />
                </div>
                <span className="text-xs font-medium text-[#0E1A2B]">{item}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-[#0E1A2B]">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Parlons de votre projet
            </h2>
            <p className="text-[#94A3B8] mb-8 leading-relaxed">
              Contactez l'équipe FLOW pour échanger sur votre besoin en climatisation et ventilation.
            </p>
            <Link
              to="/contact"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="equipe-cta-contact"
            >
              Demander un devis <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
